import {
    type ComponentType,
    useContext,
    useEffect,
    useRef,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type ICarouselItemProps from "./type";
import type {
    CarouselItemWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize
} from "../../../../core/hooks";
import type {
    CarouselPartContentProps
} from "../../type";
import {
    CarouselItemContext,
    withCarouselContext
} from "../../context";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    setCarouselNodeInert
} from "../../util";

const CarouselItemContent: FC<CarouselPartContentProps<ICarouselItemProps>> = ({
    "aria-label": ariaLabel,
    children,
    context,
    label,
    style,
    ...props
}) => {
    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const index = useContext(CarouselItemContext);

    const nodeRef = useRef<View>(null);

    const isActive = index >= context.visibleRange.start && index <= context.visibleRange.end;

    const {
        container: containerDynamicStyle
    } = useStyles({
        itemSize: context.metrics.itemSize,
        orientation: context.orientation,
        isMeasured: context.isMeasured,
        perView: context.perView
    });

    useEffect(() => {
        if(Platform.OS !== "web") {
            return;
        }

        setCarouselNodeInert({
            node: nodeRef.current,
            isInert: !isActive
        });
    }, [
        isActive
    ]);

    const position = localize("x-of-y", [
        formatNumber({
            locale: activeLocale,
            value: index + 1
        }),
        formatNumber({
            locale: activeLocale,
            value: context.count
        })
    ]);

    const webProps: CarouselItemWebPropsType = {
        "aria-roledescription": localize("slide")
    };

    return <View
        {...props}
        {...webProps}
        aria-label={ariaLabel ?? (label ? `${position}: ${label}` : position)}
        importantForAccessibility={isActive ? "auto" : "no-hide-descendants"}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-hidden={!isActive}
        ref={nodeRef}
        role="group"
    >
        {children}
    </View>;
};

const CarouselItem: ComponentType<ICarouselItemProps> = withCarouselContext("item", CarouselItemContent);
export default CarouselItem;
